import {
  faChevronDown,
  faChevronRight,
  faCreditCard,
  faFileShield,
  faPlus,
  faTimeline,
  faBinoculars
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import NotificationChannel from '@/channels/NotificationsChannel';
import { userChannelName } from '@/channels/utils/channelNames';
import type { AutomatedInsuranceOptions } from '@/components/AutomatedInsuranceOptionsForm';
import InsurancePolicyCard, {
  type AutomatedInsuranceRun,
  type VerifyRequest
} from '@/components/InsurancePolicyCard';
import NewVerificationModal from '@/components/NewVerificationModal';
import PolicyEditor from '@/components/PolicyEditor';
import { policyFinancialsSnapshot } from '@/components/PolicyFinancials';
import AppContext from '@/contexts/appContext';
import { useChannelData } from '@/hooks/useChannel';
import useInsuranceDeepLinks from '@/hooks/useInsuranceDeepLinks';
import { insurancePolicyDetailQueryKey } from '@/hooks/useInsurancePolicyDetail';
import useInsurancePolicySummaries, {
  insurancePolicySummariesQueryKey
} from '@/hooks/useInsurancePolicySummaries';
import PersonContext from '@/pages/Person/PersonContext';
import type { IncomingAppMessage, ToastMessage } from '@/types/appMessage';
import type { InsurancePolicy } from '@/types/insurancePolicy';
import { IN_PROGRESS_STATUS } from '@/types/insuranceVerification';
import { Button, vendorDisplayName } from '@/ui';

import './styles.css';
import { isInactivePolicy } from './utils/isInactivePolicy';
import { sortInsurancePolicies } from './utils/sortInsurancePolicies';

const VOB_COMPLETE_PREFIX = 'vob-complete-';
const VOB_FAILURE_PREFIX = 'vob-failure-';
const SLOW_AFTER_MS = 10 * 60 * 1000;
const IN_PROGRESS_REFETCH_MS = 15 * 1000;

type VobMessageKind = 'complete' | 'failure';

const VobToastListener = ({
  userId,
  onVobMessage
}: {
  userId: string;
  onVobMessage: (kind: VobMessageKind, policyId: string, message: ToastMessage) => void;
}) => {
  const { channelData } = useChannelData<IncomingAppMessage>({
    channelId: userChannelName(userId),
    channelName: 'insurance-panel-vob',
    shouldDetach: false
  });

  useEffect(() => {
    const collapseId = channelData?.collapse_id;
    if (!collapseId) return;

    if (collapseId.startsWith(VOB_COMPLETE_PREFIX)) {
      onVobMessage('complete', collapseId.slice(VOB_COMPLETE_PREFIX.length), channelData);
    } else if (collapseId.startsWith(VOB_FAILURE_PREFIX)) {
      onVobMessage('failure', collapseId.slice(VOB_FAILURE_PREFIX.length), channelData);
    }
  }, [channelData, onVobMessage]);

  return null;
};

const CardSkeleton = () => (
  <div aria-busy="true" className="rounded-lg border border-uniform-gray-300 bg-white">
    <div className="flex items-center gap-2.5 border-b border-uniform-gray-100 px-4 py-3.5">
      <div className="h-4 w-4 rounded-full bg-uniform-gray-200" />
      <div className="h-3.5 w-28 rounded-full bg-uniform-gray-200" />
      <div className="h-3.5 w-16 rounded-full bg-uniform-gray-100" />
      <div className="grow" />
      <div className="h-8 w-20 rounded-md bg-uniform-gray-200" />
    </div>
    <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <div className="flex flex-col gap-2.5">
        <div className="h-3 w-4/5 rounded-full bg-uniform-gray-200" />
        <div className="h-3 w-2/3 rounded-full bg-uniform-gray-200" />
        <div className="h-3 w-1/2 rounded-full bg-uniform-gray-200" />
      </div>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <div className="h-16 rounded-md bg-uniform-gray-100" />
        <div className="h-16 rounded-md bg-uniform-gray-100" />
        <div className="h-16 rounded-md bg-uniform-gray-100" />
        <div className="h-16 rounded-md bg-uniform-gray-100" />
      </div>
    </div>
  </div>
);

const InsurancePanel = () => {
  const { currentUser } = useContext(AppContext);
  const {
    person: { id: personId, has_run_discovery, most_recent_insurance_discovery },
    refetch: refetchPerson
  } = useContext(PersonContext);
  const queryClient = useQueryClient();
  const { memberBillingTimelineUrl, memberVobListUrl } = useInsuranceDeepLinks(personId);

  const [automatedRuns, setAutomatedInsuranceRuns] = useState<
    Record<string, AutomatedInsuranceRun>
  >({});
  const [verifying, setVerifying] = useState<{
    policy: InsurancePolicy;
    request?: VerifyRequest;
  } | null>(null);
  const [isAddingPolicy, setIsAddingPolicy] = useState(false);
  const [areInactiveOpen, setAreInactiveOpen] = useState(false);

  const hasLocalRunInFlight = Object.values(automatedRuns).some(
    (run) => run.state === 'running' || run.state === 'slow'
  );

  const { data: policies, isLoading, refetch } = useInsurancePolicySummaries(personId);

  const loadedPolicyIds = useRef<Set<string>>(new Set());
  const [inProgressSeenAt, setInProgressSeenAt] = useState<Record<string, number>>({});

  useEffect(() => {
    loadedPolicyIds.current = new Set(
      (policies ?? []).flatMap((policy) => (policy.id ? [policy.id] : []))
    );

    setInProgressSeenAt((seen) => {
      const next: Record<string, number> = {};
      (policies ?? []).forEach((policy) => {
        if (policy.id && policy.verification_status === IN_PROGRESS_STATUS) {
          next[policy.id] = seen[policy.id] ?? Date.now();
        }
      });

      const isUnchanged =
        Object.keys(next).length === Object.keys(seen).length &&
        Object.entries(next).every(([id, at]) => seen[id] === at);
      return isUnchanged ? seen : next;
    });
  }, [policies]);

  useEffect(() => {
    const backendPollUntil = Math.max(
      0,
      ...Object.values(inProgressSeenAt).map((seenAt) => seenAt + SLOW_AFTER_MS)
    );
    const isBackendPollActive = backendPollUntil > Date.now();
    if (!hasLocalRunInFlight && !isBackendPollActive) return;

    const interval = setInterval(() => refetch(), IN_PROGRESS_REFETCH_MS);
    const stop = hasLocalRunInFlight
      ? undefined
      : setTimeout(() => clearInterval(interval), backendPollUntil - Date.now());

    return () => {
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [hasLocalRunInFlight, inProgressSeenAt, refetch]);

  useEffect(() => {
    if (!Object.values(automatedRuns).some((run) => run.state === 'running')) return;

    const interval = setInterval(() => {
      setAutomatedInsuranceRuns((runs) => {
        const next = { ...runs };
        let changed = false;

        Object.entries(runs).forEach(([policyId, run]) => {
          if (
            run.state === 'running' &&
            run.startedAt &&
            Date.now() - new Date(run.startedAt).getTime() > SLOW_AFTER_MS
          ) {
            next[policyId] = { ...run, state: 'slow' };
            changed = true;
          }
        });

        return changed ? next : runs;
      });
    }, 30 * 1000);

    return () => clearInterval(interval);
  }, [automatedRuns]);

  const handleAutomatedRunStarted = (
    policy: InsurancePolicy,
    options: AutomatedInsuranceOptions
  ) => {
    if (!policy.id) return;

    setAutomatedInsuranceRuns((runs) => ({
      ...runs,
      [policy.id as string]: {
        state: 'running',
        options,
        startedAt: new Date().toISOString(),
        startedBy: currentUser
          ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
          : undefined,
        previousFinancials: policyFinancialsSnapshot(policy, policy.last_verification)
      }
    }));
  };

  const handleVobMessage = useCallback(
    (kind: VobMessageKind, policyId: string, message: ToastMessage) => {
      if (!loadedPolicyIds.current.has(policyId)) return;

      queryClient.invalidateQueries({ queryKey: insurancePolicySummariesQueryKey(personId) });
      queryClient.invalidateQueries({ queryKey: insurancePolicyDetailQueryKey(policyId) });
      refetchPerson();
      setAutomatedInsuranceRuns((runs) => {
        const existing = runs[policyId];
        if (kind === 'complete') {
          return { ...runs, [policyId]: { ...existing, state: 'succeeded' } };
        }
        return { ...runs, [policyId]: { ...existing, state: 'failed', errorText: message.text } };
      });
    },
    [personId, queryClient, refetchPerson]
  );

  const sortedPolicies = useMemo(() => sortInsurancePolicies(policies ?? []), [policies]);
  const activePolicies = useMemo(
    () => sortedPolicies.filter((policy) => !isInactivePolicy(policy)),
    [sortedPolicies]
  );
  const inactivePolicies = useMemo(() => sortedPolicies.filter(isInactivePolicy), [sortedPolicies]);

  const handleVerify = useCallback(
    (policy: InsurancePolicy, request?: VerifyRequest) => setVerifying({ policy, request }),
    []
  );
  const handleRefreshRequested = useCallback(() => {
    refetch();
  }, [refetch]);

  const discoveredCount =
    most_recent_insurance_discovery?.discovered_policy_verifications?.length ?? 0;
  const discoveryDate = most_recent_insurance_discovery?.requested_at
    ? new Date(most_recent_insurance_discovery.requested_at).toLocaleDateString('en-US')
    : null;
  const firstDiscoveredId =
    most_recent_insurance_discovery?.discovered_policy_verifications?.[0]?.id;
  const discoveryTimelineUrl = most_recent_insurance_discovery
    ? memberBillingTimelineUrl({
        targetDate: most_recent_insurance_discovery.requested_at,
        event: firstDiscoveredId
          ? { type: 'external_policy_verification', id: firstDiscoveredId }
          : { type: 'external_verification', id: most_recent_insurance_discovery.id }
      })
    : null;

  const renderCard = (policy: InsurancePolicy) => (
    <InsurancePolicyCard
      key={policy.id}
      policy={policy}
      personId={personId}
      automatedRun={policy.id ? automatedRuns[policy.id] : null}
      onVerify={handleVerify}
      onRefreshRequested={handleRefreshRequested}
    />
  );

  return (
    <div className="flex flex-col gap-3">
      {currentUser && (
        <NotificationChannel user={currentUser}>
          <VobToastListener userId={currentUser.id} onVobMessage={handleVobMessage} />
        </NotificationChannel>
      )}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 px-1">
        <div className="flex items-center gap-2 text-sm text-uniform-gray-500">
          {has_run_discovery && most_recent_insurance_discovery && discoveredCount === 0 && (
            <>
              <FontAwesomeIcon icon={faBinoculars} className="h-3.5 w-3.5" />
              <span>{`Automated Discovery returned 0 policies${discoveryDate ? ` · ${discoveryDate}` : ''}`}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm font-medium">
          {memberVobListUrl() && (
            <Link
              to={memberVobListUrl() as string}
              className="inline-flex items-center gap-1.5 text-blue-700">
              <FontAwesomeIcon icon={faFileShield} className="h-3.5 w-3.5" />
              All VOBs
            </Link>
          )}
          {memberBillingTimelineUrl() && (
            <Link
              to={memberBillingTimelineUrl() as string}
              className="inline-flex items-center gap-1.5 text-blue-700">
              <FontAwesomeIcon icon={faTimeline} className="h-3.5 w-3.5" />
              Billing & VOB Timeline
            </Link>
          )}
        </div>
      </div>

      {has_run_discovery && most_recent_insurance_discovery && discoveredCount > 0 && (
        <div className="flex items-center gap-2.5 rounded-md bg-blue-50 px-3 py-2.5 text-sm text-blue-900">
          <FontAwesomeIcon icon={faBinoculars} className="h-4 w-4 shrink-0 text-blue-700" />
          <span className="grow">
            Automated Discovery found{' '}
            <strong>{`${discoveredCount} ${discoveredCount === 1 ? 'policy' : 'policies'}`}</strong>
            {discoveryDate && ` on ${discoveryDate}`}
            {most_recent_insurance_discovery.source &&
              ` via ${vendorDisplayName(most_recent_insurance_discovery.source)}`}
          </span>
          <Link
            to={discoveryTimelineUrl ?? `/people/${personId}/insurance`}
            className="shrink-0 font-medium">
            Review discovered ↗
          </Link>
        </div>
      )}

      {isLoading && <CardSkeleton />}

      {!isLoading && sortedPolicies.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-uniform-gray-300 bg-white px-4 py-8 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-uniform-gray-100 text-uniform-gray-600">
            <FontAwesomeIcon icon={faCreditCard} className="h-5 w-5" />
          </div>
          <span className="text-sm font-semibold">No insurance on file</span>
          <span className="text-sm text-uniform-gray-500">Add a policy to verify coverage.</span>
        </div>
      )}

      {activePolicies.map(renderCard)}

      {inactivePolicies.length > 0 && (
        <>
          <button
            type="button"
            aria-expanded={areInactiveOpen}
            onClick={() => setAreInactiveOpen((open) => !open)}
            className="flex items-center gap-2 self-start px-1 py-1 text-sm text-uniform-gray-600">
            <FontAwesomeIcon
              icon={areInactiveOpen ? faChevronDown : faChevronRight}
              className="h-3 w-3"
            />
            <span className="font-semibold">{`Inactive policies (${inactivePolicies.length})`}</span>
            <span className="text-uniform-gray-500">
              {inactivePolicies[0]?.carrier}
              {inactivePolicies[0]?.plan_end_date &&
                ` · plan ended ${new Date(`${inactivePolicies[0].plan_end_date}T00:00`).toLocaleDateString('en-US')}`}
            </span>
          </button>
          {areInactiveOpen && inactivePolicies.map(renderCard)}
        </>
      )}

      {isAddingPolicy ? (
        <div className="rounded-lg border border-uniform-gray-300 bg-white p-4">
          <PolicyEditor onClose={() => setIsAddingPolicy(false)} />
        </div>
      ) : (
        <div>
          <Button
            variant="outline"
            color="neutral"
            size="sm"
            leftIcon={faPlus}
            onClick={() => setIsAddingPolicy(true)}>
            Add policy
          </Button>
        </div>
      )}

      {verifying && (
        <NewVerificationModal
          policy={verifying.policy}
          open
          onClose={() => setVerifying(null)}
          initialTab={verifying.request?.tab}
          initialShowOptions={verifying.request?.showOptions}
          onAutomatedRunStarted={(options) => handleAutomatedRunStarted(verifying.policy, options)}
        />
      )}
    </div>
  );
};

export default React.memo(InsurancePanel);
